import React, { useState, MouseEvent, useCallback, memo, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaSpinner } from 'react-icons/fa';
import { IconBaseProps } from 'react-icons';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled(motion.div)`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  background: var(--bg-primary);
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: var(--text-primary);
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
  z-index: 2;

  &:hover {
    background: var(--bg-secondary);
    color: var(--accent-color);
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Image = styled.img`
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
  border-radius: 4px;
  opacity: 0;
  transition: opacity 0.3s ease;
`;

const LoadingSpinner = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: var(--accent-color);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
`;

const ErrorMessage = styled.div`
  color: var(--text-primary);
  text-align: center;
  padding: 2rem;
  font-size: 1.1rem;
`;

interface CertificationImageProps {
  imagePath: string;
  isOpen: boolean;
  onClose: () => void;
}

const CertificationImage = memo<CertificationImageProps>(({ imagePath, isOpen, onClose }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  const handleImageLoad = useCallback(() => {
    setIsLoading(false);
  }, []);

  const handleImageError = useCallback(() => {
    setIsLoading(false);
    setError(true);
  }, []);

  const handleModalClick = useCallback((e: MouseEvent) => {
    e.stopPropagation();
  }, []);

  const handleClose = useCallback(() => {
    setIsLoading(true);
    setError(false);
    onClose();
  }, [onClose]);

  // Reset state when image path changes
  useEffect(() => {
    setIsLoading(true);
    setError(false);
  }, [imagePath]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
      return () => window.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, handleClose]);

  const CloseIcon = FaTimes as React.ComponentType<IconBaseProps>;
  const SpinnerIcon = FaSpinner as React.ComponentType<IconBaseProps>;

  return (
    <AnimatePresence>
      {isOpen && (
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
        >
          <ModalContent
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            onClick={handleModalClick}
          >
            <CloseButton onClick={handleClose}>
              <CloseIcon size={20} />
            </CloseButton>
            <ImageContainer>
              {isLoading && (
                <LoadingSpinner>
                  <SpinnerIcon className="spin" />
                </LoadingSpinner>
              )}
              {error ? (
                <ErrorMessage>
                  Failed to load image. Please try again.
                </ErrorMessage>
              ) : (
                <Image 
                  src={imagePath} 
                  alt="Certification" 
                  onLoad={handleImageLoad}
                  onError={handleImageError}
                  style={{ opacity: isLoading ? 0 : 1 }}
                  loading="lazy"
                />
              )}
            </ImageContainer>
          </ModalContent>
        </ModalOverlay>
      )}
    </AnimatePresence>
  );
});

CertificationImage.displayName = 'CertificationImage';

export default CertificationImage; 